import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  ArrowLeftIcon,
  EnvelopeIcon,
  PhoneIcon,
  AcademicCapIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  UserCircleIcon,
  LinkIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import { studentAPI, facultyAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { college } = useAuth();
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user details
  useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoading(true);
        // Try fetching from students first
        const studentResponse = await studentAPI.getStudents({ search: '' });
        const students = studentResponse.data?.data?.students || [];
        let foundUser = students.find(s => s._id === userId);

        if (!foundUser) {
          // Try fetching from faculty
          const facultyResponse = await facultyAPI.getFaculty({ search: '' });
          const faculty = facultyResponse.data?.data?.faculty || [];
          foundUser = faculty.find(f => f._id === userId);
        }

        if (foundUser) {
          setUser(foundUser);
        } else {
          toast.error('User not found');
          navigate(-1);
        }
      } catch (error) {
        console.error('Error fetching user:', error);
        toast.error('Failed to load user details');
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchUser();
    }
  }, [userId, navigate]);

  // Promote to Ambassador mutation
  const promoteAmbassadorMutation = useMutation(studentAPI.promoteToAmbassador, {
    onSuccess: () => {
      toast.success('Student promoted to Ambassador successfully');
      queryClient.invalidateQueries('students');
      queryClient.invalidateQueries('faculty');
      queryClient.invalidateQueries('dashboard');
      // Update local user state
      setUser(prev => ({ ...prev, role: 'Ambassador', permissions: { ...prev.permissions } }));
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to promote to ambassador');
    },
  });

  // Demote from Ambassador mutation
  const demoteAmbassadorMutation = useMutation(studentAPI.demoteAmbassador, {
    onSuccess: () => {
      toast.success('Ambassador demoted to Student successfully');
      queryClient.invalidateQueries('students');
      queryClient.invalidateQueries('faculty');
      queryClient.invalidateQueries('dashboard');
      // Update local user state - clear permissions
      setUser(prev => ({
        ...prev,
        role: 'Student',
        permissions: {
          allowedBranches: [],
          canApproveStudents: false,
          canManageClubs: false,
          canViewAnalytics: false,
          canManagePosts: false
        }
      }));
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to demote ambassador');
    },
  });

  const handlePromoteToAmbassador = () => {
    if (window.confirm('Are you sure you want to promote this student to Ambassador? They will get special privileges to manage clubs.')) {
      promoteAmbassadorMutation.mutate(userId);
    }
  };

  const handleDemoteAmbassador = () => {
    if (window.confirm('Are you sure you want to demote this ambassador back to Student? They will lose all ambassador privileges and permissions.')) {
      demoteAmbassadorMutation.mutate(userId);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <LoadingSpinner text="Loading user details..." />;
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <UserCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">User not found</h3>
        <p className="mt-1 text-sm text-gray-500">The requested user could not be found.</p>
        <button onClick={() => navigate(-1)} className="btn-primary mt-4">
          Go Back
        </button>
      </div>
    );
  }

  const hasPermissions = user.permissions && (
    user.permissions.canApproveStudents ||
    user.permissions.canManageClubs ||
    user.permissions.canViewAnalytics ||
    user.permissions.canManagePosts
  );

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Profile</h1>
            <p className="mt-1 text-sm text-gray-500">
              Complete user information and management
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Profile Overview */}
        <div className="lg:col-span-1 space-y-6">
          {/* Profile Card */}
          <div className="card">
            <div className="p-6 text-center">
              {/* Profile Picture */}
              <div className="flex justify-center mb-4">
                {user.profilePicture ? (
                  <img
                    src={user.profilePicture}
                    alt={user.name}
                    className="h-32 w-32 rounded-full object-cover border-4 border-gray-200"
                  />
                ) : (
                  <div className="h-32 w-32 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center border-4 border-gray-200">
                    <span className="text-4xl font-bold text-white">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
              </div>

              {/* Name and Role */}
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{user.name}</h2>

              {/* Status Badges */}
              <div className="flex flex-wrap justify-center gap-2 mb-4">
                <span className={`badge ${
                  user.role === 'Student' ? 'badge-blue' :
                  user.role === 'Ambassador' ? 'badge-purple' :
                  'badge-green'
                }`}>
                  {user.role}
                </span>
                <span className={`badge ${
                  user.isVerified ? 'badge-green' :
                  user.isRejected ? 'badge-red' :
                  'badge-yellow'
                }`}>
                  {user.isVerified ? '✓ Approved' : user.isRejected ? '✗ Rejected' : '⏳ Pending'}
                </span>
                {user.emailVerified && (
                  <span className="badge badge-green">
                    Email Verified
                  </span>
                )}
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Clubs</p>
                  <p className="text-lg font-bold text-gray-900">{user.clubs?.length || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Member Since</p>
                  <p className="text-lg font-bold text-gray-900">
                    {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Status Information */}
          {(user.isVerified || user.isRejected) && (
            <div className="card">
              <div className="p-6">
                <div className="flex items-center space-x-2 mb-4">
                  {user.isVerified ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircleIcon className="h-5 w-5 text-red-500" />
                  )}
                  <h3 className="text-sm font-semibold text-gray-900">
                    {user.isVerified ? 'Approval Information' : 'Rejection Information'}
                  </h3>
                </div>

                <div className="space-y-3">
                  {user.isVerified && user.approvedAt && (
                    <>
                      <div>
                        <p className="text-xs text-gray-500">Approved On</p>
                        <p className="text-sm text-gray-900">{formatDate(user.approvedAt)}</p>
                      </div>
                      {user.approvedBy && (
                        <div>
                          <p className="text-xs text-gray-500">Approved By</p>
                          <p className="text-sm text-gray-900">
                            {user.approvedBy.name || 'College Admin'}
                          </p>
                        </div>
                      )}
                    </>
                  )}

                  {user.isRejected && (
                    <>
                      {user.rejectedAt && (
                        <div>
                          <p className="text-xs text-gray-500">Rejected On</p>
                          <p className="text-sm text-gray-900">{formatDate(user.rejectedAt)}</p>
                        </div>
                      )}
                      {user.rejectionReason && (
                        <div>
                          <p className="text-xs text-gray-500">Reason</p>
                          <p className="text-sm text-gray-900">{user.rejectionReason}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Detailed Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Information */}
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <EnvelopeIcon className="h-5 w-5 mr-2 text-gray-400" />
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-start space-x-3">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Email Address</p>
                      <p className="text-sm text-gray-900 break-all">{user.email}</p>
                      {user.emailVerified && (
                        <span className="text-xs text-green-600 flex items-center mt-1">
                          <CheckCircleIcon className="h-3 w-3 mr-1" />
                          Verified
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {user.mobileNumber && (
                  <div>
                    <div className="flex items-start space-x-3">
                      <PhoneIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-1">Mobile Number</p>
                        <p className="text-sm text-gray-900">{user.mobileNumber}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Academic Information */}
          <div className="card">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <AcademicCapIcon className="h-5 w-5 mr-2 text-gray-400" />
                Academic Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-start space-x-3">
                    <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Branch/Department</p>
                      <p className="text-sm font-medium text-gray-900">{user.branch}</p>
                    </div>
                  </div>
                </div>
                {user.rollNumber && (
                  <div>
                    <div className="flex items-start space-x-3">
                      <DocumentTextIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-1">Roll Number</p>
                        <p className="text-sm font-medium text-gray-900">{user.rollNumber}</p>
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  <div className="flex items-start space-x-3">
                    <CalendarIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Registration Date</p>
                      <p className="text-sm text-gray-900">{formatDate(user.createdAt)}</p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="flex items-start space-x-3">
                    <ClockIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1">Last Updated</p>
                      <p className="text-sm text-gray-900">
                        {formatDate(user.updatedAt || user.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Social Links */}
          {(user.linkedin || user.github || user.website) && (
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <LinkIcon className="h-5 w-5 mr-2 text-gray-400" />
                  Social Links
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {user.linkedin && (
                    <a
                      href={user.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                      </svg>
                      LinkedIn
                    </a>
                  )}
                  {user.github && (
                    <a
                      href={user.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                      </svg>
                      GitHub
                    </a>
                  )}
                  {user.website && (
                    <a
                      href={user.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                    >
                      <LinkIcon className="h-5 w-5 mr-2" />
                      Website
                    </a>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Permissions (for Faculty/Ambassador) */}
          {(user.role === 'Faculty' || user.role === 'Ambassador') && (
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <ShieldCheckIcon className="h-5 w-5 mr-2 text-gray-400" />
                  Permissions & Access
                </h3>

                {hasPermissions ? (
                  <div className="space-y-4">
                    {/* Allowed Branches */}
                    {user.permissions.allowedBranches && user.permissions.allowedBranches.length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                          Allowed Branches
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {user.permissions.allowedBranches.map((branch) => (
                            <span key={branch} className="badge badge-blue">
                              {branch}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Capabilities */}
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                        Capabilities
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className={`p-3 rounded-lg border ${
                          user.permissions.canApproveStudents
                            ? 'bg-green-50 border-green-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex items-center">
                            {user.permissions.canApproveStudents ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                            )}
                            <span className={`text-sm font-medium ${
                              user.permissions.canApproveStudents ? 'text-green-900' : 'text-gray-500'
                            }`}>
                              Approve Students
                            </span>
                          </div>
                        </div>

                        <div className={`p-3 rounded-lg border ${
                          user.permissions.canManageClubs
                            ? 'bg-purple-50 border-purple-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex items-center">
                            {user.permissions.canManageClubs ? (
                              <CheckCircleIcon className="h-5 w-5 text-purple-600 mr-2" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                            )}
                            <span className={`text-sm font-medium ${
                              user.permissions.canManageClubs ? 'text-purple-900' : 'text-gray-500'
                            }`}>
                              Manage Clubs
                            </span>
                          </div>
                        </div>

                        <div className={`p-3 rounded-lg border ${
                          user.permissions.canViewAnalytics
                            ? 'bg-blue-50 border-blue-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex items-center">
                            {user.permissions.canViewAnalytics ? (
                              <CheckCircleIcon className="h-5 w-5 text-blue-600 mr-2" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                            )}
                            <span className={`text-sm font-medium ${
                              user.permissions.canViewAnalytics ? 'text-blue-900' : 'text-gray-500'
                            }`}>
                              View Analytics
                            </span>
                          </div>
                        </div>

                        <div className={`p-3 rounded-lg border ${
                          user.permissions.canManagePosts
                            ? 'bg-orange-50 border-orange-200'
                            : 'bg-gray-50 border-gray-200'
                        }`}>
                          <div className="flex items-center">
                            {user.permissions.canManagePosts ? (
                              <CheckCircleIcon className="h-5 w-5 text-orange-600 mr-2" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-gray-400 mr-2" />
                            )}
                            <span className={`text-sm font-medium ${
                              user.permissions.canManagePosts ? 'text-orange-900' : 'text-gray-500'
                            }`}>
                              Manage Posts
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Assignment Info */}
                    {user.permissions.assignedAt && (
                      <div className="pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-500">
                          Permissions assigned on {formatDate(user.permissions.assignedAt)}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <ExclamationTriangleIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">No permissions assigned yet</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Club Memberships */}
          {user.clubs && user.clubs.length > 0 && (
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <UserGroupIcon className="h-5 w-5 mr-2 text-gray-400" />
                  Club Memberships ({user.clubs.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {user.clubs.map((clubMembership) => (
                    <div
                      key={clubMembership._id}
                      className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      {clubMembership.club?.logo ? (
                        <div className="text-3xl">{clubMembership.club.logo}</div>
                      ) : (
                        <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
                          <span className="text-lg font-bold text-white">
                            {clubMembership.club?.name?.charAt(0) || 'C'}
                          </span>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {clubMembership.club?.name || 'Unknown Club'}
                        </p>
                        {clubMembership.memberRole && (
                          <p className="text-xs text-purple-600 font-medium">
                            {clubMembership.memberRole}
                          </p>
                        )}
                        <p className="text-xs text-gray-500">
                          Joined {new Date(clubMembership.joinedAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          {user.isVerified && (
            <div className="card">
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Management Actions
                </h3>
                <div className="flex flex-wrap gap-3">
                  {user.role === 'Student' && (
                    <button
                      onClick={handlePromoteToAmbassador}
                      disabled={promoteAmbassadorMutation.isLoading}
                      className="btn-primary inline-flex items-center"
                    >
                      {promoteAmbassadorMutation.isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Promoting...
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                          </svg>
                          Promote to Ambassador
                        </>
                      )}
                    </button>
                  )}
                  {user.role === 'Ambassador' && (
                    <button
                      onClick={handleDemoteAmbassador}
                      disabled={demoteAmbassadorMutation.isLoading}
                      className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors inline-flex items-center"
                    >
                      {demoteAmbassadorMutation.isLoading ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Demoting...
                        </>
                      ) : (
                        <>
                          <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                          </svg>
                          Demote to Student
                        </>
                      )}
                    </button>
                  )}
                </div>
                {user.role === 'Ambassador' && (
                  <p className="text-sm text-gray-500 mt-3 flex items-start">
                    <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Demoting will remove all ambassador privileges and permissions assigned to this user.</span>
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailPage;
